import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Button } from 'primereact/button'
import {
  FileUpload,
  FileUploadHandlerEvent,
  FileUploadHeaderTemplateOptions,
  FileUploadOptions,
  FileUploadProps,
} from 'primereact/fileupload'
import { classNames } from 'primereact/utils'

import { useToastContext } from '../../../../../contexts'
import { useFileStream } from '../../../../../data/tenantFiles/hooks'
import { getHandleableError } from '../../../../../utils/errors/isXiaError'
import {
  EInvoiceConfig,
  useEInvoiceConfig,
} from '../../../../selfservice/settings/data'
import { EInvoiceError, requestCreateEInvoice } from '../../../data'
import { UploadDialog } from './dialog'

export const EInvoiceFileUpload: React.FC = () => {
  const { t, i18n } = useTranslation('eInvoiceOverview')
  const { showToast } = useToastContext()
  const navigate = useNavigate()

  const [isDownloadingExample, setIsDownloadingExample] =
    useState<boolean>(false)
  const [isTrialMode, setIsTrialMode] = useState<boolean>(false)
  const fileUploadRef = useRef<FileUpload>(null)

  const [locationUrl, setLocationUrl] = useState<string>('')
  const [retry, setRetry] = useState<string>('')
  const [isDialogVisible, setIsDialogVisible] = useState<boolean>(false)

  const { data: eInvoiceConfig, isLoading, error } = useEInvoiceConfig()
  const {
    data: eInvoiceExample,
    isLoading: isEInvoiceExampleLoading,
    error: eInvoiceExampleError,
  } = useFileStream(`webedi/einvoice/docs/einvoice-example.pdf`)
  const {
    data: eInvoiceGuideline,
    isLoading: isEInvoiceGuidelineLoading,
    error: eInvoiceGuidelineError,
  } = useFileStream(
    `webedi/einvoice/docs/einvoice-guideline-${i18n.language}.pdf`,
  )

  if (isLoading) return null

  const handleDownloadExampleEInvoice = async () => {
    if (isDownloadingExample || isEInvoiceExampleLoading) return // Prevent multiple clicks and check loading state

    const exampleBlob = eInvoiceExample?.body || eInvoiceExample

    setIsDownloadingExample(true)

    try {
      const url = window.URL.createObjectURL(exampleBlob)
      const link = document.createElement('a')
      link.href = url
      link.download = `einvoice-example.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      showToast({
        severity: 'error',
        summary: t(
          'fileUpload.testing.downloadExampleButton.download.messages.failure.summary',
        ),
        detail: t(
          'fileUpload.testing.downloadExampleButton.download.messages.failure.detail',
        ),
      })
    } finally {
      // Small delay to prevent rapid clicking
      setTimeout(() => {
        setIsDownloadingExample(false)
      }, 500)
    }
  }

  const handleOpenGuideline = async () => {
    if (isEInvoiceGuidelineLoading) return // Check loading state

    const guidelineBlob = eInvoiceGuideline?.body || eInvoiceGuideline

    try {
      const url = window.URL.createObjectURL(guidelineBlob)
      window.open(url, '_blank')
    } catch (error) {
      showToast({
        severity: 'error',
        summary: t('fileUpload.info.guideline.open.messages.failure.summary'),
        detail: t('fileUpload.info.guideline.open.messages.failure.detail'),
      })
    }
  }

  const headerTemplate = (options: FileUploadHeaderTemplateOptions) => {
    const { chooseButton } = options

    return (
      <div className="flex justify-content-center pb-5 px-3">
        {chooseButton}
      </div>
    )
  }

  const emptyTemplate = (props: FileUploadProps) => {
    return (
      <div className="flex align-items-center text-center flex-column">
        <span className="pi pi-cloud-upload mb-2 text-primary border-circle text-7xl	"></span>

        <span className="mb-2 text-lg font-bold">{t('fileUpload.title')}</span>

        <span className="mb-4 text-sm text-color-secondary flex justify-content-center flex-wrap gap-2">
          <span>
            {t('fileUpload.info.fileType', {
              allowedFileTypes: getAllowedFileTypes(eInvoiceConfig).join(', '),
            })}
          </span>

          <span>•</span>

          <span>
            {t('fileUpload.info.fileSize', {
              maxFileSize: eInvoiceConfig?.maxFileSize,
              eInvoiceFormats: getEInvoiceFormats(eInvoiceConfig).join(', '),
            })}
          </span>

          <span>•</span>

          <span>
            {t('fileUpload.info.eInvoiceFormats', {
              eInvoiceFormats: getEInvoiceFormats(eInvoiceConfig).join(', '),
            })}
          </span>

          <span>•</span>

          <span
            role="button"
            className={classNames('text-400', {
              'cursor-pointer text-primary hover:underline':
                !!eInvoiceGuideline &&
                !isEInvoiceGuidelineLoading &&
                !eInvoiceGuidelineError,
              'cursor-not-allowed opacity-50':
                isEInvoiceGuidelineLoading || !!eInvoiceGuidelineError,
            })}
            onClick={handleOpenGuideline}
          >
            {t('fileUpload.info.guideline.label')}
          </span>
        </span>
      </div>
    )
  }

  const chooseOptions: FileUploadOptions = {
    label: t('fileUpload.select.label'),
    icon: 'pi pi-plus',
  }

  const validateFile = (file: File): boolean => {
    // Check file size
    const maxSize = getAllowedFileSizeInBytes(eInvoiceConfig?.maxFileSize)

    if (file.size > maxSize) {
      showToast({
        severity: 'error',
        // sticky: true,
        summary: t('fileUpload.validation.fileSize.summary'),
        detail: t('fileUpload.validation.fileSize.detail', {
          maxFileSizeInMB: eInvoiceConfig?.maxFileSize,
        }),
      })
      return false
    }

    // Check file format
    const acceptedFormats = getAcceptedFileFormats(eInvoiceConfig).split(',')
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase()
    const fileMimeType = file.type.toLowerCase()

    const isValidFormat = acceptedFormats.some(format => {
      const formatLower = format.toLowerCase()
      return formatLower === fileExtension || formatLower === fileMimeType
    })

    if (!isValidFormat) {
      showToast({
        severity: 'error',
        // sticky: true,
        summary: t('fileUpload.validation.fileType.summary'),
        detail: t('fileUpload.validation.fileType.detail', {
          allowedFileTypes: getAllowedFileTypes(eInvoiceConfig).join(', '),
        }),
      })
      return false
    }

    return true
  }

  const handleUpload = async (event: FileUploadHandlerEvent) => {
    const file = event.files[0]

    // Validate file before processing
    if (!validateFile(file)) {
      // Remove the file from the upload component
      fileUploadRef.current?.clear()
      return
    }

    setIsDialogVisible(true)

    try {
      const response = await requestCreateEInvoice(file, isTrialMode)

      // Set location url
      if (response && response.locationUrl) {
        setLocationUrl(response.locationUrl)
      }

      // Set retry
      if (response && response.retry) {
        setRetry(response.retry)
      }
    } catch (error: any) {
      // If status code is 423 it means a virus/malware was found
      if (error?.status === 423 || error?.response?.status === 423) {
        // The FetchError stores the response body in .info
        const responseData = error.info || {}
        const i18nMessage = responseData.i18nmessage || {}

        // Create an EInvoiceError object with the expected structure
        const virusError: EInvoiceError = {
          FileName: file.name,
          FileSizeInBytes: file.size,
          InvoiceFormat: null,
          InvoiceVersion: 'Unknown',
          Status: 0,
          Results: [
            {
              Id: 'virus-error',
              Level: 'Error',
              ValidationType: 'Virus',
              Description: 'Malicious',
              Detail: responseData.detail,
              TranslationKey: {
                Key: i18nMessage.Key,
                Parameters: i18nMessage.Parameters,
                Severity: i18nMessage.Severity,
              },
            },
          ],
        }

        // Clean up UI state before navigating
        fileUploadRef.current?.clear()
        setIsDialogVisible(false)

        // Navigate to the error page with the virus error
        navigate('/e-invoices/errors', {
          state: { errors: virusError },
        })

        return // Important: return here to prevent further error handling
      }

      // For everything else: normal error handling
      let handleableError = getHandleableError(error)
      let errorMessage = handleableError.message

      // Remove the file from the upload component
      fileUploadRef.current?.clear()

      setIsDialogVisible(false)

      showToast({
        severity: 'error',
        sticky: true,
        summary: t('fileUpload.upload.messages.failure.summary'),
        detail: errorMessage,
      })
    } finally {
      setIsTrialMode(false)
    }
  }

  const handleTestUpload = () => {
    setIsTrialMode(true)

    // Trigger the file upload dialog
    const chooseButton = fileUploadRef.current?.getInput()
    if (chooseButton) {
      chooseButton.click()
    }
  }

  return (
    <>
      <div className="flex justify-content-end flex-wrap gap-1 pb-2">
        <Button
          label={t('fileUpload.testing.testInvoiceButton')}
          text
          size="small"
          icon="pi pi-file-check"
          onClick={handleTestUpload}
        />
        <Button
          label={t('fileUpload.testing.downloadExampleButton.label')}
          text
          size="small"
          icon="pi pi-download"
          onClick={handleDownloadExampleEInvoice}
          loading={isDownloadingExample || isEInvoiceExampleLoading}
          disabled={!!eInvoiceExampleError}
        />
      </div>

      <UploadDialog
        locationUrl={locationUrl}
        retry={retry}
        isDialogVisible={isDialogVisible}
        setIsDialogVisible={setIsDialogVisible}
      />

      <FileUpload
        id="e-invoice-file-upload"
        ref={fileUploadRef}
        // UI
        headerTemplate={headerTemplate}
        emptyTemplate={emptyTemplate}
        itemTemplate={emptyTemplate}
        chooseOptions={chooseOptions}
        className="flex flex-column-reverse bg-blue-50 border-round-lg mb-8 border-round-lg border-2 border-dashed border-primary"
        contentClassName="bg-blue-50 border-transparent border-none border-round-lg pb-0"
        // Accept criteria
        accept={getAcceptedFileFormats(eInvoiceConfig)}
        // Only one file
        multiple={false}
        // Custom upload
        auto // Automatically upload after selection
        customUpload
        uploadHandler={handleUpload}
      />
    </>
  )
}

const getAllowedFileTypes = (
  eInvoiceConfig: EInvoiceConfig | undefined,
): string[] => {
  if (!eInvoiceConfig) return []

  const allowedFileTypes: string[] = []

  if (eInvoiceConfig?.xInc.active) allowedFileTypes.push('XML')
  if (eInvoiceConfig?.zugf.active) allowedFileTypes.push('PDF')

  return allowedFileTypes
}

const getEInvoiceFormats = (
  eInvoiceConfig: EInvoiceConfig | undefined,
): string[] => {
  if (!eInvoiceConfig) return []

  const formats: string[] = []

  if (eInvoiceConfig?.xInc.active) {
    const comparer = eInvoiceConfig.xInc.versionComparer === 0 ? '=' : '>='
    formats.push(`XRechnung ${comparer} ${eInvoiceConfig.xInc.version}`)
  }
  if (eInvoiceConfig?.zugf.active) {
    const comparer = eInvoiceConfig.zugf.versionComparer === 0 ? '=' : '>='
    formats.push(`ZUGFeRD ${comparer} ${eInvoiceConfig.zugf.version}`)
  }

  return formats
}

const getAcceptedFileFormats = (eInvoiceConfig?: EInvoiceConfig): string => {
  const allowedFormats: string[] = []

  if (!eInvoiceConfig) return ''

  if (eInvoiceConfig?.xInc.active)
    allowedFormats.push('.xml', 'application/xml', 'text/xml')

  if (eInvoiceConfig?.zugf.active)
    allowedFormats.push('.pdf', 'application/pdf')

  return allowedFormats.join(',')
}

const getAllowedFileSizeInBytes = (maxFileSizeInMB?: number): number => {
  return (maxFileSizeInMB || 0) * 1024 * 1024 // Convert MB to bytes
}
